import "./ConfusionPairsTable.css";

/**
 * Phase 18 WU-18.E4 — confused-pairs table.
 *
 * Pairs are computed server-side from audit entries; cold tier returns [].
 * When the response reports `tier: "cold"` AND rows are empty, show the
 * dedicated cold-tier note (monthly rollups don't retain pair data).
 */
export interface ConfusionPairRow {
  fieldName: string | null;
  ocrValue: string | null;
  keyedValue: string | null;
  occurrences: number;
}

export interface ConfusionPairsTableProps {
  rows: ConfusionPairRow[];
  tier?: "hot" | "cold";
}

export function ConfusionPairsTable({ rows, tier }: ConfusionPairsTableProps) {
  if (tier === "cold" && rows.length === 0) {
    return (
      <div
        className="confusion-pairs__cold"
        data-testid="confusion-pairs-cold"
      >
        Confusion pairs aren't retained in monthly rollups. Narrow the range
        to the last 90 days to see raw OCR → keyed pairs.
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        className="confusion-pairs__empty"
        data-testid="confusion-pairs-empty"
      >
        No confused pairs in this period.
      </div>
    );
  }

  const sorted = rows.slice().sort((a, b) => b.occurrences - a.occurrences);

  return (
    <table className="analytics-table" data-testid="confusion-pairs">
      <thead>
        <tr>
          <th>Field</th>
          <th>OCR Value</th>
          <th>Keyed Value</th>
          <th>Occurrences</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((row, i) => {
          const key = `${row.fieldName ?? ""}|${row.ocrValue ?? ""}|${row.keyedValue ?? ""}|${i}`;
          return (
            <tr
              key={key}
              data-testid={`confusion-pair-row-${i}`}
            >
              <td style={{ color: "var(--color-text-primary)" }}>
                {row.fieldName ?? "—"}
              </td>
              <td className="confusion-pairs__value">{row.ocrValue ?? "—"}</td>
              <td className="confusion-pairs__value">{row.keyedValue ?? "—"}</td>
              <td>{row.occurrences.toLocaleString()}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
